import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import type {
    CreateProductCategoryRequest,
    ListProductCategoriesParams,
    PatchProductCategoryRequest,
} from '@/lib/api/product-category-api'
import { productCategoryApi } from '@/lib/api/product-category-api'
import { productKeys } from '@/hooks/use-product-query'
import { ApiError } from '@/lib/api/client'
import { resolveApiErrorMessage } from '@/lib/api-error-messages'
import { m } from '@/lib/paraglide/messages'

/** Lỗi có mã (vd PRODUCT_CATEGORY_CODE_TAKEN) → câu FE; còn lại giữ như cũ. */
export function productCategoryErrorText(
    err: unknown,
    fallback: string,
): string {
    if (err instanceof ApiError) {
        const text = resolveApiErrorMessage(err.code, err.params)
        if (text) return text
    }
    return (err as { message?: string } | null)?.message || fallback
}

export const productCategoryKeys = {
    all: ['product-categories'] as const,
    list: (params: ListProductCategoriesParams | undefined) =>
        [...productCategoryKeys.all, 'list', params ?? {}] as const,
    detail: (id: string) => [...productCategoryKeys.all, 'detail', id] as const,
}

export function useProductCategories(params?: ListProductCategoriesParams) {
    return useQuery({
        queryKey: productCategoryKeys.list(params),
        queryFn: () => productCategoryApi.list(params),
    })
}

export function useCreateProductCategory() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (data: CreateProductCategoryRequest) =>
            productCategoryApi.create(data),
        onSuccess: () => {
            toast.success(m.product_category_create_success())
            void qc.invalidateQueries({ queryKey: productCategoryKeys.all })
        },
        onError: (err: unknown) => {
            toast.error(
                productCategoryErrorText(err, m.product_category_save_error()),
            )
        },
    })
}

export function usePatchProductCategory() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({
            id,
            data,
        }: {
            id: string
            data: PatchProductCategoryRequest
        }) => productCategoryApi.patch(id, data),
        onSuccess: () => {
            toast.success(m.product_category_update_success())
            void qc.invalidateQueries({ queryKey: productCategoryKeys.all })
            // Đổi tên danh mục là đổi cả tên hiện trên danh sách sản phẩm.
            void qc.invalidateQueries({ queryKey: productKeys.all })
        },
        onError: (err: unknown) => {
            toast.error(
                productCategoryErrorText(err, m.product_category_save_error()),
            )
        },
    })
}

export function useDeleteProductCategory() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => productCategoryApi.remove(id),
        onSuccess: () => {
            toast.success(m.product_category_delete_success())
            void qc.invalidateQueries({ queryKey: productCategoryKeys.all })
        },
        onError: (err: unknown) => {
            const msg = (err as { message?: string })?.message
            toast.error(msg || m.product_category_delete_error())
        },
    })
}
